import { api } from './api';

const productIds = {
  monthly: 'gd_premium_monthly',
  yearly: 'gd_premium_yearly',
  plusMonthly: 'gd_premium_plus_monthly',
  plusYearly: 'gd_premium_plus_yearly',
  seedling: 'gd_ai_credits_10',
  garden: 'gd_ai_credits_30',
  gold100: 'gd_growgold_100',
  gold275: 'gd_growgold_275',
  gold550: 'gd_growgold_550',
};

function plugin() {
  const native = window.Capacitor?.isNativePlatform?.();
  const billing = window.Capacitor?.Plugins?.GrowDailyBilling;
  if (!native || !billing) throw new Error('Google Play Billing is unavailable in this build');
  return billing;
}

// Google renders the price and checkout. The returned token is verified by the
// GrowDaily server before it grants an entitlement.
export async function buyWithGooglePlay(key, kind) {
  const productId = productIds[key];
  if (!productId) throw new Error('Unknown Google Play product');
  const billing = plugin();
  const type = kind === 'subs' ? 'subs' : 'inapp';
  const queried = await billing.products({ productIds: [productId], type });
  if (!queried?.products?.some((item) => item.id === productId)) {
    throw new Error('This product is not available in Google Play yet');
  }
  const account = (await api.get('/play-billing/account')).data.obfuscated_account_id;
  const purchase = await billing.purchase({ productId, obfuscatedAccountId: account });
  if (purchase?.pending) throw new Error('Payment is pending. No balance has been added yet. Use Restore purchases once Google completes it.');
  if (!purchase?.purchaseToken) throw new Error('Google Play returned no purchase token');
  if ((await api.get('/play-billing/account')).data.obfuscated_account_id !== account) {
    throw new Error('Account changed. Sign back into the purchasing account and restore purchases.');
  }
  return api.post('/play-billing/verify', {
    product_id: productId,
    purchase_token: purchase.purchaseToken,
    kind: type,
  });
}

export async function loadPlayPrices() {
  const billing = plugin();
  const output = {};
  // Serial connection/query avoids racing BillingClient.startConnection.
  for (const type of ['subs', 'inapp']) {
    const ids = Object.values(productIds).filter(id => type === 'subs' ? id.includes('premium') : !id.includes('premium'));
    const result = await billing.products({ productIds: ids, type });
    for (const product of result?.products || []) output[product.id] = product;
  }
  return Object.fromEntries(Object.entries(productIds).map(([key, id]) => [key, output[id]]));
}

export async function restoreGooglePlay() {
  const billing = plugin();
  if (!billing.restore) throw new Error('Update the Android app to restore purchases.');
  const account = (await api.get('/play-billing/account')).data.obfuscated_account_id;
  const owned = await billing.restore();
  const result = { restored: 0, pending: 0, failed: 0 };
  for (const purchase of owned?.purchases || []) {
    if (!Object.values(productIds).includes(purchase.productId)) continue;
    if (purchase.pending) { result.pending++; continue; }
    if ((await api.get('/play-billing/account')).data.obfuscated_account_id !== account) {
      throw new Error('Account changed during restore. Try again in the purchasing account.');
    }
    try {
      await api.post('/play-billing/verify', { product_id: purchase.productId,
        purchase_token: purchase.purchaseToken, kind: purchase.kind });
      result.restored++;
    } catch { result.failed++; }
  }
  return result;
}
